import React, {
  memo, useCallback, useEffect, useMemo,
} from 'react';
import { cloneDeep, find, first } from 'lodash';
import { IDateRangeSettings } from '@frontend/app/components';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@frontend/shadcn/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@frontend/shadcn/components/ui/avatar';
import { getColorFromName } from '@frontend/app/refresh-components/NameCell';
import { cn } from '@frontend/shadcn/lib/utils';
import { Tag } from '@frontend/app/refresh-components';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCircleInfo, faCoins } from '@fortawesome/free-solid-svg-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { TooltipArrow, TooltipBody, TooltipHeader, TooltipItem, TooltipItemValue, TooltipTitle } from '@frontend/applications/Social/components/tooltip';
import { AccordionContent, AccordionTrigger, AccordionItem, Accordion } from '@frontend/shadcn/components/ui/accordion';
import SalesStatsCard from './SalesStatsCard';

import { useGetAffiliateConversionHistory } from '../../hooks/useGetAffiliateConversionHistory';
import { getMultiplePayoutLabel, getPayoutLabel } from '../OfferSummaryCard/hooks';
import { AffliateLevelStats, TSelectedAffliate } from '../MemberTable/types';
import { GetOfferById_offer, GetOfferById_offer_links_affiliates_boostDataForAffiliateOffer, GetOfferById_offer_promos, GetOfferById_offer_promos_affiliates, GetOfferById_offer_promos_affiliates_boostDataForAffiliateOffer } from '../../queries/types/GetOfferById';
import { OFFER_SOURCE } from '../../types/globalTypes';
import { useGetMultipleShopifyAffliateStatsQuery } from '../../hooks';
import RefreshOfferTabs from './RefreshOfferTabs';
import { getTagColor } from '../../constants/offers';
import RefreshMultipleShopifyStoreStats from './RefreshMultipleShopifyStoreStats';

// Type for memberBoosts based on the source
export type MemberBoosts =
  | GetOfferById_offer_promos_affiliates_boostDataForAffiliateOffer[]
  | GetOfferById_offer_links_affiliates_boostDataForAffiliateOffer[]
  | null;

interface IProps {
  offer: GetOfferById_offer,
  source: OFFER_SOURCE,
  isOpen: boolean,
  selectedMemberConversion: TSelectedAffliate,
  onClose: () => void,
  refresh: () => void;
  dateRangeSettings?: IDateRangeSettings;
}

export const RefreshMemberConversionDrawer: React.FC<Readonly<IProps>> = memo((props) => {
  const {
    offer, source, isOpen, selectedMemberConversion, onClose, refresh,
    dateRangeSettings,
  } = props;
  const { linkForPromo } = useClientFeatures();
  const [shortLinkForPromo, setShortLinkForPromo] = React.useState<string>(null);
  const { multiCurrency: isMultiCurrency, enableMultipleShopify: isEnabledMultipleShopify, offerBoost } = useClientFeatures();
  const affiliateStatsData = useMemo(() => {
    if (source === OFFER_SOURCE.TUNE) {
      const affiliateStats = offer.links[0]?.affiliateStats?.find((link) => link.affiliateOfferId === selectedMemberConversion?.affiliateOfferId) as unknown as AffliateLevelStats;

      // If no affiliate stats found, return null
      if (!affiliateStats) {
        return null;
      }

      // Calculate reward count from boostRewardsForAffiliate for TUNE
      const affiliateWithBoosts = offer.links[0]?.affiliates?.find(
        (affiliate) => affiliate.id === selectedMemberConversion?.affiliateOfferId,
      );

      let rewardCount = 0;
      if (affiliateWithBoosts?.boostDataForAffiliateOffer) {
        affiliateWithBoosts.boostDataForAffiliateOffer.forEach((boostData) => {
          if (boostData.boostRewardsForAffiliate) {
            rewardCount += boostData.boostRewardsForAffiliate.filter(
              (reward) => reward.status === 'APPROVED'
            ).length;
          }
        });
      }

      // Only set properties if affiliateStats exists
      if (affiliateStats) {
        affiliateStats.rewardCount = rewardCount || 0;

        // Calculate total reward spend amount from boostDataForAffiliateOffer
        const totalRewardSpendAmount = affiliateWithBoosts?.boostDataForAffiliateOffer?.reduce((total, boostData) => {
          return total + (boostData.rewardSpendAmount || 0);
        }, 0) || 0;
        affiliateStats.rewardSpendAmount = totalRewardSpendAmount;
      }

      return affiliateStats;
    } else {
      const affiliateStats = cloneDeep(offer.promos[0]?.affiliateStats.find((promo) => promo.affiliateOfferId === selectedMemberConversion?.affiliateOfferId)) as unknown as AffliateLevelStats;

      if (offer.isPromoLink && linkForPromo && affiliateStats) {
        // add clicks to the stats
        const affiliateId = find(
          offer.promos[0].affiliates,
          (aff: GetOfferById_offer_promos_affiliates) => aff.id === selectedMemberConversion?.affiliateOfferId,
        )?.affiliateId;
        const affiliateOfferLinkStats = (first(offer.links).affiliateStats.find(
          (affiliate) => affiliate.affiliateId === affiliateId,
        ) as unknown) as AffliateLevelStats;
        affiliateStats.clicks = affiliateOfferLinkStats?.clicks || 0;
        affiliateStats.conversions += affiliateOfferLinkStats?.nonDuplicateConversions || 0;
        affiliateStats.payoutEarned += affiliateOfferLinkStats?.payoutEarned || 0;
        affiliateStats.payoutMade += affiliateOfferLinkStats?.payoutMade || 0;
        affiliateStats.sales += affiliateOfferLinkStats?.sales || 0;
        affiliateStats.avgSale = affiliateStats.sales / affiliateStats.conversions;
      }

      // Calculate reward count from boostRewardsForAffiliate - moved outside conditional
      const affiliateWithBoosts = offer.promos[0]?.affiliates.find(
        (aff: GetOfferById_offer_promos_affiliates) => aff.id === selectedMemberConversion?.affiliateOfferId,
      );
      let rewardCount = 0;
      if (affiliateWithBoosts?.boostDataForAffiliateOffer) {
        affiliateWithBoosts.boostDataForAffiliateOffer.forEach((boostData) => {
          if (boostData.boostRewardsForAffiliate) {
            rewardCount += boostData.boostRewardsForAffiliate.filter(
              (reward) => reward.status === 'APPROVED'
            ).length;
          }
        });
      }

        // Only set properties if affiliateStats exists
      if (affiliateStats) {
        affiliateStats.rewardCount = rewardCount || 0;

        // Calculate total reward spend amount from boostDataForAffiliateOffer
        const totalRewardSpendAmount = affiliateWithBoosts?.boostDataForAffiliateOffer?.reduce((total, boostData) => {
          return total + (boostData.rewardSpendAmount || 0);
        }, 0) || 0;
        affiliateStats.rewardSpendAmount = totalRewardSpendAmount;
      }

      return affiliateStats;
    }
  }, [source, selectedMemberConversion, offer.promos, linkForPromo, offer.isPromoLink, offer.links]);

  useEffect(() => {
    if (selectedMemberConversion && offer.isPromoLink && linkForPromo) {
      const affiliateId = find(
        offer.promos[0]?.affiliates,
        (aff: GetOfferById_offer_promos_affiliates) => aff.id === selectedMemberConversion?.affiliateOfferId,
      )?.affiliateId;

      const affiliateLinkInfo = first(offer.links)?.affiliates?.find((aff) => aff.affiliateId === affiliateId);
      if (affiliateLinkInfo) {
        setShortLinkForPromo(affiliateLinkInfo.affiliateShortLink);
      }
    }
  }, [source, selectedMemberConversion, offer.promos, offer.links, linkForPromo, offer.isPromoLink]);

  const baseCurrencyWithoutMulipleShopify = useMemo(() => {
    if (offer.promos?.length) {
      return first(offer.promos)?.stats?.baseCurrencies || [];
    }
    return [];
  }, [offer.promos]);
  const isMultipleShopifyOffer = useMemo(() => {
    if (offer.promos?.length) {
      return first(offer.promos)?.multiShopifyEnabled || false;
    }
    return false;
  }, [offer.promos]);
  const multipleShopifyOfferStats = useGetMultipleShopifyAffliateStatsQuery({
    fetchPolicy: 'network-only',
    variables: {
      affiliateOfferPromoId: selectedMemberConversion?.affiliateOfferId,
      startDate: dateRangeSettings?.dateRange?.startDate,
      endDate: dateRangeSettings?.dateRange?.endDate,
    },
    skip: !selectedMemberConversion?.affiliateOfferId,
  });
  const { data: affliateConversionHistory, loading: isLoadingConversionHistory, refetch } = useGetAffiliateConversionHistory({
    fetchPolicy: 'network-only',
    variables: {
      affiliateOfferId: selectedMemberConversion?.memberAffiliateOfferId,
      offerSource: offer.isPromoLink ? OFFER_SOURCE.PROMO_LINK : source,
    },
    skip: !selectedMemberConversion?.memberAffiliateOfferId,
  });
  const refetchData = useCallback(() => {
    if (selectedMemberConversion?.memberAffiliateOfferId) {
      refetch({
        affiliateOfferId: selectedMemberConversion?.memberAffiliateOfferId,
        offerSource: !offer.isPromoLink ? source : OFFER_SOURCE.PROMO_LINK,
      });
    }
  }, [selectedMemberConversion?.memberAffiliateOfferId, source, refetch, offer.isPromoLink]);

  const promoOffer: GetOfferById_offer_promos = offer?.promos?.length ? first(offer.promos) : null;

  const memberBoosts: MemberBoosts = useMemo(() => {
    if (source === OFFER_SOURCE.SHOPIFY) {
      return offer.promos[0]?.affiliates.find((affiliate) => affiliate.id === selectedMemberConversion?.affiliateOfferId)?.boostDataForAffiliateOffer;
    }
    if (source === OFFER_SOURCE.TUNE) {
      return offer.links[0]?.affiliates.find((affiliate) => affiliate.id === selectedMemberConversion?.affiliateOfferId)?.boostDataForAffiliateOffer;
    }
    return null;
  }, [offer.promos, selectedMemberConversion?.affiliateOfferId, source, offer.links]);

  const payoutLabel = useMemo(() => {
    if (selectedMemberConversion?.payout) {
      return getMultiplePayoutLabel(
        selectedMemberConversion.payout.label,
        selectedMemberConversion.payout.payoutType,
        selectedMemberConversion.payout.flatPayout,
        selectedMemberConversion.payout.percentPayout,
        selectedMemberConversion.payout.offerPayoutRules,
      );
    }

    if (promoOffer) {
      return getPayoutLabel(
        promoOffer.payoutType,
        promoOffer.flatPayout,
        promoOffer.percentPayout,
        null,
      );
    }

    return '-';
  }, [selectedMemberConversion?.payout, promoOffer]);
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="flex flex-col h-full bg-grey-0 p-0 gap-0 w-[576px] !max-w-[576px] overflow-x-hidden">
        <SheetHeader className="p-4 flex items-center gap-2 flex-row">
          <SheetTitle className="!m-0 text-base font-medium">Member Overview</SheetTitle>
        </SheetHeader>
        <div className="flex items-center gap-2 px-4 py-2 bg-grey-1 border-t border-b border-grey-2 h-12">
          <div className="flex items-center gap-2">
            <Avatar className="rounded-full w-8 h-8">
              <AvatarImage
                src={selectedMemberConversion?.imageUrl}
                alt={selectedMemberConversion?.name}
                className="rounded-full h-6 w-6"
              />
              <AvatarFallback className={cn(getColorFromName(selectedMemberConversion?.name), 'text-white h-6 w-6')}>
                {selectedMemberConversion?.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </AvatarFallback>
            </Avatar>
            <Tooltip>
              <TooltipTrigger>
                <p className="text-sm font-medium text-grey-6 m-0">{selectedMemberConversion?.name.length > 40 ? `${selectedMemberConversion?.name.substring(0, 40)}...` : selectedMemberConversion?.name}</p>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max">
                {selectedMemberConversion?.name}
              </TooltipContent>
            </Tooltip>
          </div>
          <div>
            <Tag label={`${selectedMemberConversion?.status} Till ${new Date(offer.expirationDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`} bgColor={getTagColor(selectedMemberConversion?.status)} textColor="text-primary" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-4">
            <Card className="flex items-start gap-4 w-full bg-grey-1 rounded-2xl p-3 border-0">
              <CardContent className="p-0 w-full flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCoins} className="text-grey-6" />
                  <p className="m-0 text-sm font-medium text-grey-6"> Sales Stats </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <FontAwesomeIcon className="text-grey-4 cursor-pointer" icon={faCircleInfo} />
                    </TooltipTrigger>
                    <TooltipContent
                      className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
                      side="bottom"
                      align="start"
                      sideOffset={5}
                      role="tooltip"
                      aria-live="polite"
                    >
                      <TooltipHeader>
                        <TooltipTitle>Sales Stats</TooltipTitle>
                      </TooltipHeader>
                      <TooltipBody>
                        <TooltipItem>
                          <TooltipItemValue>
                            {source === OFFER_SOURCE.SHOPIFY && (isMultipleShopifyOffer || isMultiCurrency) ? (
                              <span>
                                Sales are calculated based on the currency exchange rates during the time of conversion.
                              </span>
                            ) : (
                              <span>
                                Sales stats are calculated based on the sales made by the member.
                              </span>
                            )}
                          </TooltipItemValue>
                        </TooltipItem>
                      </TooltipBody>
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3">
                  <SalesStatsCard widgetType="count" title="Clicks" value={affiliateStatsData?.clicks} />
                  <SalesStatsCard widgetType="count" title="Conversions" value={affiliateStatsData?.conversions} />
                  <SalesStatsCard widgetType="currency" title="Total Sales" value={affiliateStatsData?.sales} secondaryValue={isMultiCurrency ? affiliateStatsData?.salesBase : null} currency={baseCurrencyWithoutMulipleShopify[0]} />
                  <SalesStatsCard widgetType="currency" title="Avg. Sales" value={affiliateStatsData?.avgSale} secondaryValue={isMultiCurrency ? affiliateStatsData?.avgSaleBase : null} currency={baseCurrencyWithoutMulipleShopify[0]} />
                  <SalesStatsCard widgetType="currency" title="Payout Earned" value={affiliateStatsData?.payoutEarned} secondaryValue={isMultiCurrency ? affiliateStatsData?.payoutEarnedBase : null} currency={baseCurrencyWithoutMulipleShopify[0]} />
                  <SalesStatsCard widgetType="currency" title="Payout Due" value={affiliateStatsData?.payoutMade} />
                  {offer.boosts?.length && (<SalesStatsCard widgetType="count" title="Reward Claimed" value={affiliateStatsData?.rewardCount} />)}
                  {offer.boosts?.length && (<SalesStatsCard widgetType="currency" title="Spend in Rewards" value={affiliateStatsData?.rewardSpendAmount || 0} currency={baseCurrencyWithoutMulipleShopify[0]} />)}
                </div>

                {source === OFFER_SOURCE.SHOPIFY && isEnabledMultipleShopify && !!multipleShopifyOfferStats.data?.affliateStats?.length && (
                  <div>
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="item-1" className="border-0 m-0">
                        <AccordionTrigger className="hover:no-underline p-0 m-0 group [&>svg:last-child]:hidden">
                          <span className="flex items-center gap-x-2 underline text-grey-6 text-sm font-medium">
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className="text-grey-6 transition-transform duration-200 group-data-[state=open]:rotate-180"
                            />
                            Store Conversion Data
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <RefreshMultipleShopifyStoreStats data={multipleShopifyOfferStats.data?.affliateStats} />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="mt-4">
              <RefreshOfferTabs
                offer={offer}
                selectedMemberConversion={selectedMemberConversion}
                shortLinkForPromo={shortLinkForPromo}
                affiliateConversionHistory={affliateConversionHistory?.affliateConversionHistory}
                source={!offer.isPromoLink ? source : OFFER_SOURCE.PROMO_LINK}
                refetchData={refetchData}
                refresh={refresh}
                payoutLabel={payoutLabel}
                isLoadingConversionHistory={isLoadingConversionHistory}
                memberBoosts={offerBoost ? memberBoosts : null}
                onClose={onClose}
              />
            </div>
          </div>
        </div>

        <SheetFooter className="justify-start sm:justify-start p-4 border-t bg-grey-0 z-[99]" />
      </SheetContent>
    </Sheet>
  );
});
